import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import NumberFormat from 'react-number-format';
import { format } from 'date-fns';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  TextField,
  Icon,
  Button,
  StepLabel,
  Step,
  Stepper,
  Grid,
  StepConnector,
  Typography,
  MenuItem,
  IconButton,
  InputAdornment
} from '@material-ui/core';
import {
  MuiPickersUtilsProvider,
  TimePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { addStorePickupLocationStatus, setNewLocationValue, removeLocationValue } from 'redux/actions/store_pickup';
import { addStorePickupLocationsApi } from 'api';
import ColorlibStepIcon from './ColorlibStepIcon';
import statesArray from './states-array.json';


const ColorlibConnector = withStyles({
  alternativeLabel: {
    top: 22,
  },
  active: {
    '& $line': {
      backgroundImage:
        'linear-gradient( 136deg, rgb(160 181 247) 0%, rgb(52 88 195) 50%, rgb(17 14 86) 100%)'
    },
  },
  completed: {
    '& $line': {
      backgroundImage:
        'linear-gradient( 136deg, rgb(160 181 247) 0%, rgb(52 88 195) 50%, rgb(17 14 86) 100%)'
    },
  },
  line: {
    height: 3,
    border: 0,
    backgroundColor: '#eaeaf0',
    borderRadius: 1,
  },
})(StepConnector);

const getSteps = () => {
  return ['Location', 'Contacts', 'Working Hours'];
};

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const zipRegRxp = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
const phoneRegExp = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

const addressValidationSchema = yup.object({
  name: yup
    .string()
    .required('Field is required'),
  position: yup
    .number()
    .required('Field is required'),
  city: yup
    .string()
    .required('Field is required'),
  postal_code: yup
    .string()
    .required('Field is required')
    .matches(zipRegRxp, 'Please enter a valid zip code (Ex: 90602 or 90602-1234)'),
  state: yup
    .string()
    .required('Field is required'),
  address_line1: yup
    .string()
    .required('Field is required')
});

const contactsValidationSchema = yup.object({
  email: yup
    .string()
    .email(),
  main_phone: yup
    .string()
    .matches(phoneRegExp, 'Phone number is not valid')
});

const workingHoursValidationSchema = yup
  .object()
  .shape({
    Monday_from: yup.string().ensure().when('Monday_to', {
      is: (Monday_to) => Monday_to?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Monday_to: yup.string().ensure().when('Monday_from', {
      is: (Monday_from) => Monday_from?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Tuesday_from: yup.string().ensure().when('Tuesday_to', {
      is: (Tuesday_to) => Tuesday_to?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Tuesday_to: yup.string().ensure().when('Tuesday_from', {
      is: (Tuesday_from) => Tuesday_from?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Wednesday_from: yup.string().ensure().when('Wednesday_to', {
      is: (Wednesday_to) => Wednesday_to?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Wednesday_to: yup.string().ensure().when('Wednesday_from', {
      is: (Wednesday_from) => Wednesday_from?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Thursday_from: yup.string().ensure().when('Thursday_to', {
      is: (Thursday_to) => Thursday_to?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Thursday_to: yup.string().ensure().when('Thursday_from', {
      is: (Thursday_from) => Thursday_from?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Friday_from: yup.string().ensure().when('Friday_to', {
      is: (Friday_to) => Friday_to?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Friday_to: yup.string().ensure().when('Friday_from', {
      is: (Friday_from) => Friday_from?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Saturday_from: yup.string().ensure().when('Saturday_to', {
      is: (Saturday_to) => Saturday_to?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Saturday_to: yup.string().ensure().when('Saturday_from', {
      is: (Saturday_from) => Saturday_from?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Sunday_from: yup.string().ensure().when('Sunday_to', {
      is: (Sunday_to) => Sunday_to?.length > 0,
      then: yup.string().required('Field is required')
    }),
    Sunday_to: yup.string().ensure().when('Sunday_from', {
      is: (Sunday_from) => Sunday_from?.length > 0,
      then: yup.string().required('Field is required')
    }),
  },
  [
    ['Monday_from', 'Monday_to'],
    ['Tuesday_from', 'Tuesday_to'],
    ['Wednesday_from', 'Wednesday_to'],
    ['Thursday_from', 'Thursday_to'],
    ['Friday_from', 'Friday_to'],
    ['Saturday_from', 'Saturday_to'],
    ['Sunday_from', 'Sunday_to']
  ]
  );

export const StepContent = ({activeStep, onStepChange, setAddNewLocation}) => {
  const dispatch = useDispatch();

  const {profile: {stores}} = useSelector((state) => state.user);
  const {newStorePickupLocation, isAutoEnableStorePickup} = useSelector((state) => state.store_pickup);
  const {customerGroupsIds = [] } = useSelector((state) => state.customers);

  const setLocationValue = (name, value) => {
    dispatch(setNewLocationValue(name, value));
  };

  const handleRemoveLocationValue = (name) => {
    dispatch(removeLocationValue(name));
  };

  const handleSaveLocation = () => {
    const storeId = [stores[0].storeId];
    const location = {
      ...newStorePickupLocation,
      store_code: newStorePickupLocation.name,
      store_id: storeId,
      customer_group: [...newStorePickupLocation.customer_group, ...customerGroupsIds]
    };
    addStorePickupLocationsApi({dispatch, location, isAutoEnableStorePickup});
    dispatch(addStorePickupLocationStatus(false));
    setAddNewLocation(false);
  };

  const initialFormValues = {
    main_phone: newStorePickupLocation?.main_phone || '',
    email: newStorePickupLocation?.email || '',
    name: newStorePickupLocation?.name  || '',
    position: newStorePickupLocation?.position || 0,
    city: newStorePickupLocation?.city || '',
    postal_code: newStorePickupLocation?.postal_code || '',
    state: newStorePickupLocation?.state || '',
    address_line1: newStorePickupLocation?.address_line1 || '',
    Monday_from: newStorePickupLocation?.hours?.find((item) => item.day === 'Monday')?.from || null,
    Monday_to: newStorePickupLocation?.hours?.find((item) => item.day === 'Monday')?.to || null,
    Tuesday_from: newStorePickupLocation?.hours?.find((item) => item.day === 'Tuesday')?.from || null,
    Tuesday_to: newStorePickupLocation?.hours?.find((item) => item.day === 'Tuesday')?.to || null,
    Wednesday_from: newStorePickupLocation?.hours?.find((item) => item.day === 'Wednesday')?.from || null,
    Wednesday_to: newStorePickupLocation?.hours?.find((item) => item.day === 'Wednesday')?.to || null,
    Thursday_from: newStorePickupLocation?.hours?.find((item) => item.day === 'Thursday')?.from || null,
    Thursday_to: newStorePickupLocation?.hours?.find((item) => item.day === 'Thursday')?.to || null,
    Friday_from: newStorePickupLocation?.hours?.find((item) => item.day === 'Friday')?.from || null,
    Friday_to: newStorePickupLocation?.hours?.find((item) => item.day === 'Friday')?.to || null,
    Saturday_from: newStorePickupLocation?.hours?.find((item) => item.day === 'Saturday')?.from || null,
    Saturday_to: newStorePickupLocation?.hours?.find((item) => item.day === 'Saturday')?.to || null,
    Sunday_from: newStorePickupLocation?.hours?.find((item) => item.day === 'Sunday')?.from || null,
    Sunday_to: newStorePickupLocation?.hours?.find((item) => item.day === 'Sunday')?.to || null,
  };

  switch (activeStep) {
    case 0:
      return (
        <Formik
          initialValues={initialFormValues}
          validationSchema={addressValidationSchema}
          onSubmit={() => onStepChange(1)}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit
          }) => (
            <form noValidate autoComplete="off" className="p-8" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6">General:</Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    className="w-full"
                    label="Title"
                    name="name"
                    value={values.name}
                    onChange={(event) => {
                      const {name, value} = event.target;

                      handleChange(event);
                      setLocationValue(name, value);
                    }}
                    onBlur={(event) => {
                      handleBlur(event);
                    }}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    className="w-full"
                    label="Position"
                    name="position"
                    value={values.position}
                    onChange={(event) => {
                      const {name, value} = event.target;

                      handleChange(event);
                      setLocationValue(name, value);
                    }}
                    onBlur={(event) => {
                      const {name, value} = event.target;

                      handleBlur(event);
                      setLocationValue(name, value);
                    }}
                    error={touched.position && Boolean(errors.position)}
                    helperText={touched.position && errors.position}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6">Store Address:</Typography>
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField
                    required
                    className="w-full"
                    label="Street, suite"
                    name="address_line1"
                    value={values.address_line1}
                    onChange={(event) => {
                      const {name, value} = event.target;

                      handleChange(event);
                      setLocationValue(name, value);
                    }}
                    onBlur={(event) => {
                      const {name, value} = event.target;

                      handleBlur(event);
                      setLocationValue(name, value);
                    }}
                    error={touched.address_line1 && Boolean(errors.address_line1)}
                    helperText={touched.address_line1 && errors.address_line1}
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField
                    required
                    className="w-full"
                    label="City"
                    name="city"
                    value={values.city}
                    onChange={(event) => {
                      const {name, value} = event.target;

                      handleChange(event);
                      setLocationValue(name, value);
                    }}
                    onBlur={(event) => {
                      const {name, value} = event.target;

                      handleBlur(event);
                      setLocationValue(name, value);
                    }}
                    error={touched.city && Boolean(errors.city)}
                    helperText={touched.city && errors.city}
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField
                    required
                    className="w-full"
                    label="Postal Code"
                    name="postal_code"
                    value={values.postal_code}
                    onChange={(event) => {
                      const {name, value} = event.target;

                      handleChange(event);
                      setLocationValue(name, value);
                    }}
                    onBlur={(event) => {
                      const {name, value} = event.target;

                      handleBlur(event);
                      setLocationValue(name, value);
                    }}
                    error={touched.postal_code && Boolean(errors.postal_code)}
                    helperText={touched.postal_code && errors.postal_code}
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <TextField
                    required
                    select
                    className="w-full"
                    label="US State"
                    name="state"
                    value={values.state}
                    onChange={(event) => {
                      const {name, value} = event.target;

                      handleChange(event);
                      setLocationValue(name, value);
                    }}
                    onBlur={(event) => {
                      const {name, value} = event.target;

                      handleBlur(event);
                      setLocationValue(name, value);
                    }}
                    error={touched.state && Boolean(errors.state)}
                    helperText={touched.state && errors.state}
                  >
                    {statesArray.map((state, index) => (
                      <MenuItem value={state.code} key={index}>{state.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <div className="pt-6">
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  Next
                </Button>
              </div>
            </form>
          )}
        </Formik>
      );
    case 1:
      return (
        <Formik
          initialValues={initialFormValues}
          validationSchema={contactsValidationSchema}
          onSubmit={() => onStepChange(0)}
        >
          {({
            values,
            errors,
            isValid,
            handleChange,
            setFieldValue,
            handleBlur,
            handleSubmit
          }) => {
            return (
              <form autoComplete="off" className="p-8" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <NumberFormat
                      format="+1 (###) ###-####"
                      mask="_"
                      className="w-full"
                      label="Store phone"
                      name="main_phone"
                      placeholder="Store phone number"
                      value={values.main_phone}
                      customInput={TextField}
                      onChange={(event) => {
                        const {name, value} = event.target;

                        setFieldValue('main_phone', value);
                        setLocationValue(name, value);
                      }}
                      onBlur={(event) => {
                        const {name, value} = event.target;

                        handleChange(event);
                        setLocationValue(name, value);
                      }}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(errors.main_phone)}
                      helperText={errors.main_phone}
                    />

                    <TextField
                      className="w-full mt-4"
                      label="Store email"
                      placeholder="Store email"
                      name="email"
                      type="email"
                      value={values.email}
                      onChange={(event) => {
                        const {name, value} = event.target;

                        handleChange(event);
                        setLocationValue(name, value);
                      }}
                      onBlur={(event) => {
                        const {name, value} = event.target;

                        handleBlur(event);
                        setLocationValue(name, value);
                      }}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(errors.email)}
                      helperText={errors.email}
                    />
                  </Grid>
                </Grid>
                <div className="mt-6">
                  <Button
                    variant="contained"
                    color="secondary"
                    type="submit"
                  >
                    Back
                  </Button>
                  <Button
                    className="ml-4"
                    variant="contained"
                    color="primary"
                    disabled={!isValid}
                    onClick={() => onStepChange(2)}
                  >
                    Next
                  </Button>
                </div>
              </form>
            );
          }}
        </Formik>
      );
    case 2:
      return (
        <Formik
          initialValues={initialFormValues}
          validationSchema={workingHoursValidationSchema}
          onSubmit={() => {
            handleSaveLocation();
            onStepChange(0);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            handleBlur,
            setFieldValue
          }) => {
            return (
              <form autoComplete="off" className="p-8" onSubmit={handleSubmit}>
                <div  className="p-8">
                  {days.map((day) => {
                    const correctDateValueFrom = values[`${day}_from`] ? new Date(`Thu Nov 10 2022 ${values[`${day}_from`]}`) : null;
                    const correctDateValueTo = values[`${day}_to`] ? new Date(`Thu Nov 10 2022 ${values[`${day}_to`]}`) : null;

                    return (
                      <Grid container spacing={3} key={day}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                          <Grid item xs={12} sm={6}>From:</Grid>
                          <Grid item xs={12} sm={6}>To:</Grid>
                          <Grid item xs={12} sm={6}>
                            <TimePicker
                              fullWidth
                              className="my-2"
                              name={`${day}_from`}
                              label={day}
                              value={correctDateValueFrom}
                              onChange={(date) => {
                                const validTime = format(date, 'HH:mm');

                                setFieldValue(`${day}_from`, validTime);
                                setLocationValue(`${day}_from`, validTime);
                              }}
                              onBlur={handleBlur}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();

                                        setFieldValue(`${day}_from`, null);
                                        handleRemoveLocationValue(`${day}_from`);
                                      }}
                                    >
                                      <Icon fontSize="small">
                                        clear
                                      </Icon>
                                    </IconButton>
                                  </InputAdornment>
                                ),
                                placeholder: '--:-- --'
                              }}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              error={touched[`${day}_to`] && Boolean(errors[`${day}_from`])}
                              helperText={touched[`${day}_to`] && errors[`${day}_from`]}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TimePicker
                              fullWidth
                              className="my-2"
                              name={`${day}_to`}
                              label={day}
                              value={correctDateValueTo}
                              onChange={(date) => {
                                const validTime = format(date, 'HH:mm');

                                setFieldValue(`${day}_to`, validTime);
                                setLocationValue(`${day}_to`, validTime);
                              }}
                              onBlur={handleBlur}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();

                                        setFieldValue(`${day}_to`, null);
                                        handleRemoveLocationValue(`${day}_from`);
                                      }}
                                    >
                                      <Icon fontSize="small">
                                        clear
                                      </Icon>
                                    </IconButton>
                                  </InputAdornment>
                                ),
                                placeholder: '--:-- --'
                              }}
                              inputProps={{
                                step: 900, // 5 min
                              }}
                              error={touched[`${day}_from`] && Boolean(errors[`${day}_to`])}
                              helperText={touched[`${day}_from`] && errors[`${day}_to`]}
                            />
                          </Grid>
                        </MuiPickersUtilsProvider>
                      </Grid>
                    );
                  })}
                  <div className="mt-6">
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => onStepChange(1)}
                    >
                      Back
                    </Button>
                    <Button
                      className="ml-4"
                      variant="contained"
                      color="primary"
                      type="submit"
                    >
                      Save Configurations
                    </Button>
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      );
    default:
      return '';
  }
};

const AddLocation = ({setAddNewLocation}) => {
  const [activeStep, setActiveStep] = useState(0);
  const steps = getSteps();

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleNextStep = (step) => setActiveStep(step);

  return (
    <div>
      <Stepper activeStep={activeStep} alternativeLabel connector={<ColorlibConnector />}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel StepIconComponent={ColorlibStepIcon}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <div>
        {activeStep === steps.length ? (
          <div>
            <div className="flex items-center mb-4">
              <Icon>done</Icon> <span className="ml-2">Done</span>
            </div>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleReset}
            >
              Add New Location
            </Button>
          </div>
        ) : (
          <div>
            <StepContent activeStep={activeStep} onStepChange={handleNextStep} setAddNewLocation={setAddNewLocation} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AddLocation;
